import {
  ICONIFY_SEARCH_URL,
  ICONIFY_BASE_URL,
  ICONIFY_SEARCH_LIMIT,
  ICON_VALUE_MAX_LEN,
} from '../constants/bookingConst';

// An icon is stored as ONE STRING: either an emoji, or an Iconify id like
// "mdi:weather-sunny". The two are told apart by the colon, which an emoji
// never contains. Same scheme as the Kawnter POS category icons.

/**
 * Whether an icon value is an Iconify id rather than an emoji.
 *
 * @param {string} [value]
 * @returns {boolean}
 */
export const isIconifyIcon = (value) => typeof value === 'string' && value.includes(':');

/**
 * The SVG URL for an Iconify id. Used as a CSS mask (see IconGlyph), so the
 * icon takes the surrounding text colour and no third-party markup is ever
 * injected into the page.
 *
 * @param {string} id - e.g. 'mdi:weather-sunny'
 * @returns {string}
 */
export const iconifyUrl = (id) => `${ICONIFY_BASE_URL}/${String(id).replace(':', '/')}.svg`;

/**
 * Trims an icon value and drops one too long to be real, so a stray paste
 * can't be written into config.
 *
 * @param {string} [value]
 * @returns {string} '' when there is no usable icon
 */
export const normalizeIconValue = (value) => {
  const icon = String(value || '').trim();
  return icon.length > 0 && icon.length <= ICON_VALUE_MAX_LEN ? icon : '';
};

/**
 * Searches Iconify for icons matching a query.
 *
 * Iconify's public API needs no key — a key shipped to a browser would be a
 * key given away. Nothing is stored here; only the id the admin picks is.
 *
 * @param {string} query - free text, e.g. 'sun'
 * @param {{signal?: AbortSignal}} [options] - to cancel a superseded keystroke
 * @returns {Promise<string[]>} Iconify ids, e.g. ['mdi:weather-sunny']
 * @throws {Error} a message fit to show the admin when the service can't be reached
 */
export const searchIcons = async (query, { signal } = {}) => {
  const term = String(query || '').trim();
  if (!term) return [];

  const url = `${ICONIFY_SEARCH_URL}?query=${encodeURIComponent(term)}&limit=${ICONIFY_SEARCH_LIMIT}`;
  let response;
  try {
    response = await fetch(url, { signal });
  } catch (error) {
    // An aborted request is the caller superseding itself, not a failure —
    // rethrown as-is so the caller can tell the two apart.
    if (error?.name === 'AbortError') throw error;
    throw new Error('Could not reach the icon service. Check the connection, or pick an emoji instead.');
  }
  if (!response.ok) {
    throw new Error('The icon service did not answer. Try again, or pick an emoji instead.');
  }

  const data = await response.json();
  return Array.isArray(data?.icons) ? data.icons : [];
};
